import { useState, useEffect } from "react";
import axios from "axios";
import { BarChart3, Loader2, Sparkles, ExternalLink, Tag } from "lucide-react";
import { logEvent } from "@/lib/events";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LearnerResults() {
  const [results, setResults] = useState([]);
  const [overall, setOverall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedbackCards, setFeedbackCards] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/learner/results`, { withCredentials: true })
      .then(({ data }) => {
        // Backend now returns { overall, results } — stay tolerant of legacy array shape.
        if (Array.isArray(data)) {
          setResults(data);
          setOverall(null);
        } else {
          setResults(data?.results || []);
          setOverall(data?.overall || null);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    axios
      .get(`${API}/learner/logic-feedback`, { withCredentials: true })
      .then(({ data }) => setFeedbackCards(data?.cards || []))
      .catch(() => {});
    logEvent("results_viewed");
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const hasAny = results.length > 0 || overall;

  return (
    <div className="space-y-6" data-testid="learner-results">
      <div>
        <h1 className="text-2xl font-heading font-bold text-gray-900">My Results</h1>
        <p className="text-sm text-gray-500 font-body mt-1">
          Track your assessment progress and scores.
        </p>
      </div>

      {!hasAny ? (
        <div className="text-center py-16 bg-white border border-gray-200 rounded-xl">
          <BarChart3 className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-400 font-body">Nothing to show yet — your scores will appear here as you complete tracks and activities.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Headline Overall Score — one grade rolled up from all scored items
              in the learner's assigned track. */}
          {overall && (
            <div
              className="rounded-xl p-5 sm:p-6 flex items-center justify-between gap-4"
              style={{ backgroundColor: "#e6faf8", border: "1px solid #99f6e4" }}
              data-testid="results-overall-score"
            >
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-body font-semibold uppercase tracking-wider" style={{ color: "#0d5a52" }}>Overall Score</p>
                <p
                  className="text-base sm:text-lg font-heading font-bold mt-1 truncate"
                  style={{ color: "#0d5a52" }}
                  data-testid="results-overall-title"
                >
                  {overall.track_name || "Your Program"}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p
                  className="text-3xl sm:text-4xl font-heading font-bold tabular-nums whitespace-nowrap"
                  style={{ color: "#0d9488" }}
                  data-testid="results-overall-score-pct"
                >
                  {overall.score_pct}%
                </p>
                <p className="text-[10px] font-body tabular-nums mt-0.5" style={{ color: "#0d5a52" }}>
                  {overall.correct}/{overall.total}
                </p>
              </div>
            </div>
          )}

        </div>
      )}

      {/* Based on your results — fires only when missed items match a logic rule.
          Lives outside the results conditional so it can still surface relevant
          feedback even if the learner has no current assessment scores. */}
      {feedbackCards.length > 0 && (
        <div className="space-y-3 pt-2" data-testid="logic-feedback-section">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" style={{ color: "var(--theme-primary, var(--primary-color, #6366F1))" }} />
            <h2 className="text-lg font-heading font-bold text-gray-900">Based on your results</h2>
          </div>
          <p className="text-sm font-body text-gray-500 -mt-1">
            Personalized feedback and resources tied to the topics you missed.
          </p>
          <div className="space-y-3">
            {feedbackCards.map((card) => (
              <div
                key={card.tag_id}
                className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 space-y-3"
                data-testid={`logic-feedback-card-${card.tag_id}`}
              >
                <div className="flex items-center gap-2">
                  <Tag className="h-3.5 w-3.5" style={{ color: "var(--theme-primary, var(--primary-color, #6366F1))" }} />
                  <span className="text-sm font-body font-semibold text-gray-900">{card.tag_name}</span>
                </div>
                <div className="space-y-2.5">
                  {(card.responses || []).map((r, idx) => (
                    <div key={idx} data-testid={`logic-feedback-response-${card.tag_id}-${idx}`}>
                      {r.type === "text" ? (
                        <p className="text-sm font-body text-gray-700 whitespace-pre-wrap leading-relaxed">
                          {r.content}
                        </p>
                      ) : (
                        <a
                          href={r.resource_url}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="inline-flex items-center gap-1.5 text-sm font-body font-medium text-indigo-600 hover:text-indigo-700 hover:underline"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          {r.resource_title || r.resource_url}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
